import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Tag, Plus, Pencil, Trash2, Check, Search, AlertCircle, Loader,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNotes } from '../../contexts/NotesContext';
import categoriesApi from '../../services/categoriesApi';
import { CATEGORY_ICONS, getCategoryIcon } from '../../utils/categoryIcons';

const PRESET_COLORS = [
  '#3b82f6', '#06b6d4', '#10b981', '#f59e0b',
  '#ef4444', '#8b5cf6', '#ec4899', '#f97316',
  '#84cc16', '#14b8a6', '#6366f1', '#a855f7',
];

const ColorPicker = ({ value, onChange }) => {
  const { t } = useTranslation('notes');
  return (
  <div className="flex flex-wrap gap-2 p-3 bg-[var(--color-bg-base)] border border-white/[0.07] rounded-xl">
    {PRESET_COLORS.map((c) => (
      <button
        key={c}
        onClick={() => onChange(c)}
        className="relative w-7 h-7 rounded-full border-2 transition-all hover:scale-110 focus:outline-none"
        style={{ backgroundColor: c, borderColor: value === c ? '#fff' : 'transparent' }}
        title={c}
      >
        {value === c && (
          <Check className="absolute inset-0 m-auto w-3.5 h-3.5 text-white" />
        )}
      </button>
    ))}
    {/* Custom color */}
    <div className="relative w-7 h-7" title={t('editor.customColor')}>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-full rounded-full cursor-pointer opacity-0 absolute inset-0"
      />
      <div
        className="w-full h-full rounded-full border-2 border-dashed border-white/30 flex items-center justify-center"
        style={{ backgroundColor: PRESET_COLORS.includes(value) ? 'transparent' : value }}
      >
        {PRESET_COLORS.includes(value) && (
          <Plus className="w-3.5 h-3.5 text-white/40" />
        )}
      </div>
    </div>
  </div>
  );
};

const IconPicker = ({ value, onChange, color }) => {
  const { t } = useTranslation('notes');
  return (
  <div className="p-3 bg-[var(--color-bg-base)] border border-white/[0.07] rounded-xl">
    <p className="text-[10px] text-[var(--color-text-muted)] font-semibold uppercase tracking-wider mb-2">{t('manageCategoriesModal.iconPickerTitle')}</p>
    <div className="grid grid-cols-10 gap-1.5">
      {/* Yok seçeneği */}
      <button
        onClick={() => onChange(null)}
        title={t('manageCategoriesModal.noIconTitle')}
        className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-all ${
          !value
            ? 'border-white/50 bg-white/10'
            : 'border-white/[0.06] hover:border-white/20 hover:bg-white/[0.04]'
        }`}
      >
        <span className="text-[var(--color-text-muted)] text-[9px] font-bold">—</span>
      </button>
      {CATEGORY_ICONS.map(({ id, Icon, label }) => (
        <button
          key={id}
          onClick={() => onChange(id)}
          title={label}
          className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-all ${
            value === id
              ? 'border-white/50 bg-white/10'
              : 'border-white/[0.06] hover:border-white/20 hover:bg-white/[0.04]'
          }`}
        >
          <Icon
            className="w-3.5 h-3.5"
            style={{ color: value === id ? color : '#7a96b0' }}
          />
        </button>
      ))}
    </div>
  </div>
  );
};

const ManageCategoriesModal = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation('notes');
  const { authToken } = useAuth();
  const { notes, updateCategoryInNotes } = useNotes();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('#3b82f6');
  const [editIcon, setEditIcon] = useState(null);

  // Add state
  const [addName, setAddName] = useState('');
  const [addColor, setAddColor] = useState('#3b82f6');
  const [addIcon, setAddIcon] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  // Delete confirm
  const [deletingId, setDeletingId] = useState(null);

  const editInputRef = useRef(null);
  const addInputRef = useRef(null);

  const noteCounts = React.useMemo(() => {
    const counts = {};
    notes.forEach((n) => {
      (n.categories || []).forEach((cat) => {
        if (cat.name) counts[cat.name] = (counts[cat.name] || 0) + 1;
      });
    });
    return counts;
  }, [notes]);

  const loadCategories = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setError('');
    try {
      const data = await categoriesApi.getAll(authToken);
      if (data.ok) {
        setCategories(data.categories || []);
        window.dispatchEvent(new CustomEvent('notesplus-categories-updated'));
      }
    } catch {
      setError(t('manageCategoriesModal.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [authToken, t]);

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      setSearch('');
      setEditingId(null);
      setShowAdd(false);
      setAddName('');
      setAddColor('#3b82f6');
      setAddIcon(null);
      setDeletingId(null);
      setError('');
    }
  }, [isOpen, loadCategories]);

  useEffect(() => {
    if (editingId && editInputRef.current) editInputRef.current.focus();
  }, [editingId]);

  useEffect(() => {
    if (showAdd && addInputRef.current) addInputRef.current.focus();
  }, [showAdd]);

  const filtered = categories.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  // ─── Add ────────────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    const name = addName.trim();
    if (!name) return;
    setSaving(true);
    setError('');
    try {
      const data = await categoriesApi.create(authToken, { name, color: addColor, icon: addIcon });
      if (!data.ok) { setError(data.message || t('manageCategoriesModal.errors.generic')); return; }
      setCategories((prev) => [...prev, data.category].sort((a, b) => a.name.localeCompare(b.name, i18n.language)));
      setAddName('');
      setAddColor('#3b82f6');
      setAddIcon(null);
      // Form stays open so user can add more categories; cancel button closes it
      window.dispatchEvent(new CustomEvent('notesplus-categories-updated'));
    } catch {
      setError(t('manageCategoriesModal.errors.addFailed'));
    } finally {
      setSaving(false);
    }
  };

  // ─── Edit start ─────────────────────────────────────────────────────────────
  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditColor(cat.color);
    setEditIcon(cat.icon || null);
    setDeletingId(null);
  };

  // ─── Edit save ──────────────────────────────────────────────────────────────
  const handleSave = async (cat) => {
    const name = editName.trim();
    if (!name) return;
    setSaving(true);
    setError('');
    try {
      const data = await categoriesApi.update(authToken, cat.id, { name, color: editColor, icon: editIcon });
      if (!data.ok) { setError(data.message || t('manageCategoriesModal.errors.generic')); return; }
      const oldName = data.oldName;
      setCategories((prev) =>
        prev
          .map((c) => (c.id === cat.id ? data.category : c))
          .sort((a, b) => a.name.localeCompare(b.name, i18n.language))
      );
      setEditingId(null);
      updateCategoryInNotes(oldName || name, name, editColor);
      window.dispatchEvent(new CustomEvent('notesplus-categories-updated'));
    } catch {
      setError(t('manageCategoriesModal.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (cat) => {
    setSaving(true);
    setError('');
    try {
      const data = await categoriesApi.remove(authToken, cat.id);
      if (!data.ok) { setError(data.message || t('manageCategoriesModal.errors.generic')); return; }
      setCategories((prev) => prev.filter((c) => c.id !== cat.id));
      setDeletingId(null);
      updateCategoryInNotes(cat.name, null, null);
      window.dispatchEvent(new CustomEvent('notesplus-categories-updated'));
    } catch {
      setError(t('manageCategoriesModal.errors.deleteFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[var(--color-bg-base)] border border-white/[0.08] rounded-2xl shadow-[0_32px_80px_rgba(0,0,0,0.7)] w-full max-w-lg flex flex-col"
            style={{ maxHeight: 'min(88vh, 720px)' }}
          >
            {/* ── Header ── */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-white/[0.07] flex-shrink-0">
              <div className="w-9 h-9 rounded-xl bg-[#2563eb]/15 flex items-center justify-center flex-shrink-0">
                <Tag className="w-4.5 h-4.5 text-[#3d8ef0]" style={{ width: 18, height: 18 }} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-[var(--color-text-primary)] font-bold text-base">{t('manageCategoriesModal.header')}</h2>
                <p className="text-[var(--color-text-muted)] text-xs mt-0.5">{t('manageCategoriesModal.countLabel', { count: categories.length })}</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-white/[0.05] rounded-xl transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* ── Search ── */}
            <div className="px-6 pt-4 pb-2 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)]" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('manageCategoriesModal.searchPlaceholder')}
                  className="w-full bg-[var(--color-bg-muted)] border border-white/[0.07] rounded-xl pl-9 pr-4 py-2.5 text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] focus:outline-none focus:border-[#3d8ef0]/50 focus:ring-1 focus:ring-[#3d8ef0]/20"
                />
              </div>
            </div>

            {/* ── Error ── */}
            {error && (
              <div className="mx-6 mb-2 flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}

            {/* ── Category List ── */}
            <div className="flex-1 overflow-y-auto px-6 py-2 min-h-0">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader className="w-6 h-6 text-[#3d8ef0] animate-spin" />
                  <p className="text-sm text-[var(--color-text-muted)]">{t('common:loading')}</p>
                </div>
              ) : filtered.length === 0 && !showAdd ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="w-14 h-14 rounded-2xl bg-[var(--color-bg-muted)] flex items-center justify-center">
                    <Tag className="w-6 h-6 text-[var(--color-text-muted)]" />
                  </div>
                  <p className="text-sm text-[var(--color-text-muted)]">
                    {search ? t('manageCategoriesModal.noSearchResults') : t('manageCategoriesModal.noCategories')}
                  </p>
                  {!search && (
                    <button
                      onClick={() => setShowAdd(true)}
                      className="text-sm text-[#3d8ef0] hover:text-[#5ca3f5] font-medium transition-colors"
                    >
                      {t('manageCategoriesModal.addFirst')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-1.5 py-1">
                  {filtered.map((cat) => {
                    const isEditing = editingId === cat.id;
                    const isDeleting = deletingId === cat.id;
                    const count = noteCounts[cat.name] || 0;
                    const CatIcon = getCategoryIcon(cat.icon);

                    return (
                      <div key={cat.id}>
                        {isEditing ? (
                          /* ── Edit Row ── */
                          <div className="bg-[var(--color-bg-muted)] border border-[#3d8ef0]/30 rounded-xl p-3 space-y-3">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border border-white/10"
                                style={{ backgroundColor: editColor + '22' }}
                              >
                                {editIcon
                                  ? (() => { const I = getCategoryIcon(editIcon); return <I className="w-4 h-4" style={{ color: editColor }} />; })()
                                  : <div className="w-3 h-3 rounded-full" style={{ backgroundColor: editColor }} />
                                }
                              </div>
                              <input
                                ref={editInputRef}
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                placeholder={t('manageCategoriesModal.namePlaceholderEdit')}
                                className="flex-1 bg-[#161f31] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] focus:outline-none focus:border-[#3d8ef0]/50 focus:ring-1 focus:ring-[#3d8ef0]/20"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSave(cat);
                                  if (e.key === 'Escape') setEditingId(null);
                                }}
                              />
                            </div>
                            <ColorPicker value={editColor} onChange={setEditColor} />
                            <IconPicker value={editIcon} onChange={setEditIcon} color={editColor} />
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => setEditingId(null)}
                                className="px-3 py-1.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-white/[0.04] rounded-lg transition-colors font-medium"
                              >
                                {t('common:cancel')}
                              </button>
                              <button
                                onClick={() => handleSave(cat)}
                                disabled={saving || !editName.trim()}
                                className="px-4 py-1.5 text-xs bg-[#2563eb] hover:bg-[#3d8ef0] disabled:opacity-50 text-white rounded-lg font-semibold transition-colors flex items-center gap-1.5"
                              >
                                {saving ? <Loader className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                {t('common:save')}
                              </button>
                            </div>
                          </div>
                        ) : isDeleting ? (
                          /* ── Delete Confirm Row ── */
                          <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                            <p className="text-sm text-[var(--color-text-primary)] mb-1">
                              {t('manageCategoriesModal.willBeDeleted', { name: cat.name })}
                            </p>
                            {count > 0 && (
                              <p className="text-xs text-[var(--color-text-muted)] mb-3">
                                {t('manageCategoriesModal.notesAffected', { count })}
                              </p>
                            )}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setDeletingId(null)}
                                className="flex-1 py-1.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-white/[0.04] rounded-lg transition-colors font-medium"
                              >
                                {t('common:cancel')}
                              </button>
                              <button
                                onClick={() => handleDelete(cat)}
                                disabled={saving}
                                className="flex-1 py-1.5 text-xs bg-red-500 hover:bg-red-400 disabled:opacity-50 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-1.5"
                              >
                                {saving ? <Loader className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                {t('common:delete')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* ── Normal Row ── */
                          <div className="group flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[var(--color-bg-muted)] transition-colors">
                            <div
                              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: cat.color + '22' }}
                            >
                              <CatIcon className="w-4 h-4" style={{ color: cat.color }} />
                            </div>
                            <span className="flex-1 text-sm text-[var(--color-text-secondary)] font-medium truncate">
                              {cat.name}
                            </span>
                            {count > 0 && (
                              <span className="text-xs text-[var(--color-text-muted)] bg-[#161f31] px-2 py-0.5 rounded-full flex-shrink-0">
                                {t('manageCategoriesModal.countBadge', { count })}
                              </span>
                            )}
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => startEdit(cat)}
                                className="p-1.5 text-[var(--color-text-muted)] hover:text-[#3d8ef0] hover:bg-[#2563eb]/10 rounded-lg transition-colors"
                                title={t('common:edit')}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => { setDeletingId(cat.id); setEditingId(null); }}
                                className="p-1.5 text-[var(--color-text-muted)] hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                title={t('common:delete')}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Add New Section ── */}
            <div className="px-6 pb-5 pt-3 border-t border-white/[0.07] flex-shrink-0">
              <AnimatePresence>
                {showAdd ? (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-3 overflow-hidden"
                  >
                    <p className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider pt-1">{t('manageCategoriesModal.newCategoryLabel')}</p>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border border-white/10"
                        style={{ backgroundColor: addColor + '22' }}
                      >
                        {addIcon
                          ? (() => { const I = getCategoryIcon(addIcon); return <I className="w-4.5 h-4.5" style={{ color: addColor, width: 18, height: 18 }} />; })()
                          : <div className="w-4 h-4 rounded-full" style={{ backgroundColor: addColor }} />
                        }
                      </div>
                      <input
                        ref={addInputRef}
                        type="text"
                        value={addName}
                        onChange={(e) => setAddName(e.target.value)}
                        placeholder={t('manageCategoriesModal.namePlaceholderAdd')}
                        className="flex-1 bg-[var(--color-bg-muted)] border border-white/[0.07] rounded-xl px-3 py-2.5 text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] focus:outline-none focus:border-[#3d8ef0]/50 focus:ring-1 focus:ring-[#3d8ef0]/20"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleAdd();
                          if (e.key === 'Escape') { setShowAdd(false); setAddName(''); }
                        }}
                      />
                    </div>
                    <ColorPicker value={addColor} onChange={setAddColor} />
                    <IconPicker value={addIcon} onChange={setAddIcon} color={addColor} />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setShowAdd(false); setAddName(''); setAddColor('#3b82f6'); setAddIcon(null); }}
                        className="flex-1 py-2 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-white/[0.04] rounded-xl transition-colors font-medium"
                      >
                        {t('common:cancel')}
                      </button>
                      <button
                        onClick={handleAdd}
                        disabled={saving || !addName.trim()}
                        className="flex-1 py-2 text-sm bg-[#2563eb] hover:bg-[#3d8ef0] disabled:opacity-50 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                      >
                        {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        {t('manageCategoriesModal.add')}
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => setShowAdd(true)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 text-sm text-[#3d8ef0] hover:text-[#5ca3f5] hover:bg-[#2563eb]/[0.06] rounded-xl border border-dashed border-[#3d8ef0]/30 hover:border-[#3d8ef0]/50 font-medium transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    {t('manageCategoriesModal.addCategoryButton')}
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ManageCategoriesModal;
